import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Card } from '@cards/Card';
import ProfilePictureAvatar from './Avatar';
import ProfilePictureCropModal from './CropModal';
import { validateProfilePictureSource, decodeProfilePictureSource, revokeDecodedProfilePicture } from './validateSource';
import {
  saveManagedProfilePicture,
  clearManagedProfilePicture,
  saveOwnProfilePicture,
  clearOwnProfilePicture,
  isCommittedResult,
  SELF_PROFILE_PICTURE_TARGET,
} from './lifecycle';
import { loadResumeState, clearResumeState } from './resumeState';
import './EditorCard.less';

function revokeIfObjectUrl(url) {
  if (url && typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Reusable Profile Picture editor card.
 * Save/Remove are independent of ordinary profile / MFA edits.
 * `mode="self"` uses JWT-resolved self routes; `mode="managed"` uses org + IDM user id.
 */
function ProfilePictureEditorCard({
  api,
  mode,
  organisationSlug,
  userId,
  displayName,
  actorId,
  initialCanonical,
  onCanonicalChange,
  storage,
  createIdempotencyKey,
}) {
  const isSelf = mode === 'self';
  const resumeTargetId = isSelf ? SELF_PROFILE_PICTURE_TARGET : userId;
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const selectButtonRef = useRef(null);
  const [canonical, setCanonical] = useState(
    initialCanonical || {
      profilePictureImageId: null,
      profilePictureUrl: null,
    },
  );
  const [candidateUrl, setCandidateUrl] = useState(null);
  const [candidateBlob, setCandidateBlob] = useState(null);
  const [sourceImage, setSourceImage] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [candidateSnapshot, setCandidateSnapshot] = useState(null);
  const [loading, setLoading] = useState(!initialCanonical);
  const [busy, setBusy] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [localError, setLocalError] = useState(null);
  const prepareKeyRef = useRef(null);
  const clearKeyRef = useRef(null);
  const liveRegionRef = useRef(null);
  const announcedStatusRef = useRef('');

  const displayedUrl = candidateUrl || canonical.profilePictureUrl;
  const displayedExpectedId =
    canonical.profilePictureImageId === undefined ? null : canonical.profilePictureImageId;
  const hasUnsavedCandidate = Boolean(candidateBlob);

  const applyCanonical = useCallback(
    next => {
      setCanonical(next);
      if (onCanonicalChange) onCanonicalChange(next);
    },
    [onCanonicalChange],
  );

  const announce = useCallback(text => {
    if (!text || text === announcedStatusRef.current) return;
    announcedStatusRef.current = text;
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = text;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (initialCanonical) {
        setLoading(false);
      } else {
        setLoading(true);
        try {
          const picture = isSelf
            ? await api.getOwnProfilePicture()
            : await api.getOrganisationUserProfilePicture(organisationSlug, userId);
          if (!cancelled) {
            applyCanonical({
              profilePictureImageId: picture.profilePictureImageId,
              profilePictureUrl: picture.profilePictureUrl,
            });
          }
        } catch (err) {
          if (!cancelled) {
            setErrorKey('loadFailed');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      }

      // Resume a retained operation after reload before allowing another upload.
      if (!storage || actorId == null) return;
      const resume = loadResumeState(storage, actorId, resumeTargetId);
      if (!resume || !resume.operationId) return;
      setBusy(true);
      try {
        if (resume.action === 'replace') {
          const observed = await api.getProfilePictureOperation(resume.operationId);
          if (cancelled) return;
          if (isCommittedResult(observed)) {
            clearResumeState(storage, actorId, resumeTargetId);
            applyCanonical({
              profilePictureImageId: observed.operation.profilePictureImageId,
              profilePictureUrl: observed.operation.profilePictureUrl,
            });
            announce(t('common:profilePicture.status.saved'));
            return;
          }
          if (observed.operation && observed.operation.status === 'awaiting_upload') {
            // Cropped Blob is gone after reload and the candidate was never uploaded —
            // abandon this uncommitted operation and ask the user to reselect.
            clearResumeState(storage, actorId, resumeTargetId);
            prepareKeyRef.current = null;
            setErrorKey('reselect');
            return;
          }
          setUpdating(true);
          announce(t('common:profilePicture.status.updating'));
          const finalised = await api.finaliseProfilePictureOperation(resume.operationId);
          if (cancelled) return;
          if (isCommittedResult(finalised)) {
            clearResumeState(storage, actorId, resumeTargetId);
            applyCanonical({
              profilePictureImageId: finalised.operation.profilePictureImageId,
              profilePictureUrl: finalised.operation.profilePictureUrl,
            });
            announce(t('common:profilePicture.status.saved'));
          } else {
            prepareKeyRef.current = resume.idempotencyKey;
            setErrorKey('saveFailed');
          }
        } else if (resume.action === 'clear') {
          clearKeyRef.current = resume.idempotencyKey;
          setUpdating(true);
          announce(t('common:profilePicture.status.updating'));
          const result = isSelf
            ? await clearOwnProfilePicture({
                api,
                displayedExpectedImageId: resume.expectedImageId,
                idempotencyKey: resume.idempotencyKey,
                actorId,
                storage,
                onPending: () => {
                  setUpdating(true);
                  announce(t('common:profilePicture.status.updating'));
                },
              })
            : await clearManagedProfilePicture({
                api,
                organisationSlug,
                userId,
                displayedExpectedImageId: resume.expectedImageId,
                idempotencyKey: resume.idempotencyKey,
                actorId,
                storage,
                onPending: () => {
                  setUpdating(true);
                  announce(t('common:profilePicture.status.updating'));
                },
              });
          if (cancelled) return;
          if (result.status === 'committed') {
            applyCanonical({
              profilePictureImageId: null,
              profilePictureUrl: null,
            });
            clearKeyRef.current = null;
            announce(t('common:profilePicture.status.removed'));
          } else if (result.status === 'conflict' || result.status === 'stale') {
            applyCanonical({
              profilePictureImageId: result.canonical.profilePictureImageId,
              profilePictureUrl: result.canonical.profilePictureUrl,
            });
            setConflict(true);
            setErrorKey(result.status === 'stale' ? 'stale' : 'conflict');
            clearKeyRef.current = null;
          } else {
            setErrorKey('removeFailed');
          }
        }
      } catch (err) {
        if (!cancelled) setErrorKey('saveFailed');
      } finally {
        if (!cancelled) {
          setBusy(false);
          setUpdating(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [
    api,
    isSelf,
    organisationSlug,
    userId,
    resumeTargetId,
    initialCanonical,
    applyCanonical,
    actorId,
    storage,
    announce,
    t,
  ]);

  useEffect(() => {
    return () => {
      revokeIfObjectUrl(candidateUrl);
    };
  }, [candidateUrl]);

  const releaseCandidate = useCallback(() => {
    setCandidateBlob(null);
    setCandidateUrl(prev => {
      revokeIfObjectUrl(prev);
      return null;
    });
    setSourceImage(prev => {
      revokeDecodedProfilePicture(prev);
      return null;
    });
  }, []);

  const handleSelectClick = () => {
    setLocalError(null);
    setConflict(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async event => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const validation = validateProfilePictureSource(file);
    if (!validation.ok) {
      setLocalError(validation.reason === 'size' ? 'fileTooLarge' : 'unsupportedType');
      return;
    }

    try {
      const image = await decodeProfilePictureSource(file);
      setCandidateSnapshot({
        blob: candidateBlob,
        url: candidateUrl,
      });
      setSourceImage(image);
      setCropOpen(true);
    } catch (err) {
      setLocalError('undecodable');
    }
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    setSourceImage(prev => {
      revokeDecodedProfilePicture(prev);
      return null;
    });
    // Restore snapshot from when the modal opened.
    if (candidateSnapshot) {
      setCandidateBlob(candidateSnapshot.blob);
      setCandidateUrl(candidateSnapshot.url);
    }
    setCandidateSnapshot(null);
  };

  const handleUsePicture = blob => {
    const url = URL.createObjectURL(blob);
    setCandidateUrl(prev => {
      // Do not revoke the snapshot URL if Cancel restored it later — only revoke prior live candidate.
      if (prev && (!candidateSnapshot || prev !== candidateSnapshot.url)) {
        revokeIfObjectUrl(prev);
      }
      return url;
    });
    setCandidateBlob(blob);
    setCropOpen(false);
    setSourceImage(prev => {
      revokeDecodedProfilePicture(prev);
      return null;
    });
    setCandidateSnapshot(null);
    setLocalError(null);
    setConflict(false);
    setErrorKey(null);
    prepareKeyRef.current = null;
  };

  const handleDiscard = () => {
    releaseCandidate();
    setLocalError(null);
    setConflict(false);
    setErrorKey(null);
    prepareKeyRef.current = null;
    clearKeyRef.current = null;
  };

  const handleSave = async () => {
    if (!candidateBlob) return;
    setBusy(true);
    setUpdating(false);
    setErrorKey(null);
    setConflict(false);

    if (!prepareKeyRef.current) {
      prepareKeyRef.current = createIdempotencyKey();
    }

    try {
      const outcome = isSelf
        ? await saveOwnProfilePicture({
            api,
            displayedExpectedImageId: displayedExpectedId,
            croppedBlob: candidateBlob,
            idempotencyKey: prepareKeyRef.current,
            actorId,
            storage,
            onPending: () => {
              setUpdating(true);
              announce(t('common:profilePicture.status.updating'));
            },
          })
        : await saveManagedProfilePicture({
            api,
            organisationSlug,
            userId,
            displayedExpectedImageId: displayedExpectedId,
            croppedBlob: candidateBlob,
            idempotencyKey: prepareKeyRef.current,
            actorId,
            storage,
            onPending: () => {
              setUpdating(true);
              announce(t('common:profilePicture.status.updating'));
            },
          });

      if (outcome.status === 'committed') {
        releaseCandidate();
        applyCanonical({
          profilePictureImageId: outcome.canonical.profilePictureImageId,
          profilePictureUrl: outcome.canonical.profilePictureUrl,
        });
        prepareKeyRef.current = null;
        setUpdating(false);
        announce(t('common:profilePicture.status.saved'));
        message.success(t('common:profilePicture.status.saved'));
      } else if (outcome.status === 'stale' || outcome.status === 'conflict') {
        releaseCandidate();
        applyCanonical({
          profilePictureImageId: outcome.canonical.profilePictureImageId,
          profilePictureUrl: outcome.canonical.profilePictureUrl,
        });
        setConflict(true);
        setErrorKey(outcome.status === 'stale' ? 'stale' : 'conflict');
        prepareKeyRef.current = null;
        announce(t('common:profilePicture.errors.conflict'));
      } else if (outcome.status === 'idempotency') {
        setErrorKey('idempotency');
        prepareKeyRef.current = null;
      } else if (outcome.status === 'expired') {
        setErrorKey('expired');
      } else if (outcome.status === 'attention') {
        setErrorKey('attention');
        setUpdating(false);
      } else {
        setErrorKey('saveFailed');
      }
    } catch (err) {
      setErrorKey('saveFailed');
    } finally {
      setBusy(false);
      setUpdating(false);
    }
  };

  const handleRemove = () => {
    Modal.confirm({
      title: t('common:profilePicture.remove.confirmTitle', { name: displayName }),
      okText: t('common:profilePicture.remove.confirmOk'),
      cancelText: t('common:profilePicture.crop.cancel'),
      onOk: async () => {
        setBusy(true);
        setErrorKey(null);
        setConflict(false);
        if (!clearKeyRef.current) {
          clearKeyRef.current = createIdempotencyKey();
        }
        try {
          const outcome = isSelf
            ? await clearOwnProfilePicture({
                api,
                displayedExpectedImageId: displayedExpectedId,
                idempotencyKey: clearKeyRef.current,
                actorId,
                storage,
                onPending: () => {
                  setUpdating(true);
                  announce(t('common:profilePicture.status.updating'));
                },
              })
            : await clearManagedProfilePicture({
                api,
                organisationSlug,
                userId,
                displayedExpectedImageId: displayedExpectedId,
                idempotencyKey: clearKeyRef.current,
                actorId,
                storage,
                onPending: () => {
                  setUpdating(true);
                  announce(t('common:profilePicture.status.updating'));
                },
              });

          if (outcome.status === 'committed') {
            releaseCandidate();
            applyCanonical({
              profilePictureImageId: null,
              profilePictureUrl: null,
            });
            clearKeyRef.current = null;
            announce(t('common:profilePicture.status.removed'));
            message.success(t('common:profilePicture.status.removed'));
          } else if (outcome.status === 'stale' || outcome.status === 'conflict') {
            releaseCandidate();
            applyCanonical({
              profilePictureImageId: outcome.canonical.profilePictureImageId,
              profilePictureUrl: outcome.canonical.profilePictureUrl,
            });
            setConflict(true);
            setErrorKey(outcome.status === 'stale' ? 'stale' : 'conflict');
            clearKeyRef.current = null;
          } else if (outcome.status === 'idempotency') {
            setErrorKey('idempotency');
            clearKeyRef.current = null;
          } else {
            setErrorKey('removeFailed');
          }
        } catch (err) {
          setErrorKey('removeFailed');
        } finally {
          setBusy(false);
          setUpdating(false);
        }
      },
    });
  };

  const statusMessage = (() => {
    if (localError) return t(`common:profilePicture.errors.${localError}`);
    if (errorKey) return t(`common:profilePicture.errors.${errorKey}`);
    if (updating) return t('common:profilePicture.status.updating');
    return null;
  })();

  const canRemove = Boolean(canonical.profilePictureUrl) && !hasUnsavedCandidate;
  const canSave = hasUnsavedCandidate;

  return (
    <Card
      className="profile-picture-editor-card"
      title={t('common:profilePicture.editor.title')}
      loading={loading}
    >
      <div className="profile-picture-editor-body">
        <ProfilePictureAvatar url={displayedUrl} displayName={displayName} size={96} />
        <div className="profile-picture-editor-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={handleFileChange}
            data-testid="profile-picture-file-input"
          />
          <Button ref={selectButtonRef} onClick={handleSelectClick} disabled={busy}>
            {canonical.profilePictureUrl || candidateUrl
              ? t('common:profilePicture.editor.replace')
              : t('common:profilePicture.editor.select')}
          </Button>
          <Button onClick={handleSave} type="primary" disabled={!canSave || busy} loading={busy && !updating}>
            {t('common:profilePicture.editor.save')}
          </Button>
          <Button onClick={handleRemove} disabled={!canRemove || busy}>
            {t('common:profilePicture.editor.remove')}
          </Button>
          <Button onClick={handleDiscard} disabled={!hasUnsavedCandidate || busy}>
            {t('common:profilePicture.editor.discard')}
          </Button>
        </div>
      </div>

      {(updating) && (
        <div className="profile-picture-editor-updating">
          <Spin size="small" /> {t('common:profilePicture.status.updating')}
        </div>
      )}

      {statusMessage ? (
        <div className="profile-picture-editor-error" role="alert">
          {statusMessage}
          {conflict ? ` ${t('common:profilePicture.errors.reapply')}` : null}
        </div>
      ) : null}

      <div className="sr-only" aria-live="polite" ref={liveRegionRef} />

      <ProfilePictureCropModal
        visible={cropOpen}
        image={sourceImage}
        invokingElement={selectButtonRef.current}
        onCancel={handleCropCancel}
        onUsePicture={handleUsePicture}
      />
    </Card>
  );
}

ProfilePictureEditorCard.propTypes = {
  api: PropTypes.shape({
    getOrganisationUserProfilePicture: PropTypes.func,
    prepareOrganisationUserProfilePicture: PropTypes.func,
    clearOrganisationUserProfilePicture: PropTypes.func,
    getOwnProfilePicture: PropTypes.func,
    prepareOwnProfilePicture: PropTypes.func,
    clearOwnProfilePicture: PropTypes.func,
    getProfilePictureOperation: PropTypes.func.isRequired,
    finaliseProfilePictureOperation: PropTypes.func.isRequired,
  }).isRequired,
  mode: PropTypes.oneOf(['managed', 'self']),
  organisationSlug: PropTypes.string,
  userId: PropTypes.number,
  displayName: PropTypes.string.isRequired,
  actorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialCanonical: PropTypes.shape({
    profilePictureImageId: PropTypes.string,
    profilePictureUrl: PropTypes.string,
  }),
  onCanonicalChange: PropTypes.func,
  storage: PropTypes.object,
  createIdempotencyKey: PropTypes.func,
};

ProfilePictureEditorCard.defaultProps = {
  mode: 'managed',
  organisationSlug: null,
  userId: null,
  actorId: 'anonymous',
  initialCanonical: null,
  onCanonicalChange: null,
  storage: typeof sessionStorage !== 'undefined' ? sessionStorage : undefined,
  createIdempotencyKey: () => uuidv4(),
};

export default ProfilePictureEditorCard;
